/**
 * ledgerStatementMappingHeaders
 *
 * The headers a statement mapping reads, in order: the line amount, match group and dimensions, the `skipRows`
 * headers, then each value source's (total, reference, received date, original total), a `row` source giving its
 * `where` header before its own. Two spellings of one column (trimmed, lower-cased) count once, the first kept, since
 * reading treats them as one column. Consumers: `ledgerStatementReceipt`, which looks each one up in the header row,
 * and `validateLedgerDefinition`, which asks a mapping's `templateHeadings` to include each one. Internal to the engine.
 */

import type { LedgerStatementMapping } from '../System.Ledger.Definition.types';

const headersOf = (source: LedgerStatementMapping['total'] | undefined): string[] => {
    if (source?.kind === 'column') return [source.header];
    return source?.kind === 'row' ? [source.where.header, source.header] : [];
};

export const ledgerStatementMappingHeaders = (mapping: LedgerStatementMapping): string[] => {
    const read = [
        mapping.lines.amount,
        ...(mapping.lines.matchGroup === undefined ? [] : [mapping.lines.matchGroup]),
        ...Object.values(mapping.lines.dimensions ?? {}),
        ...mapping.skipRows.map((test) => test.header),
        ...headersOf(mapping.total),
        ...headersOf(mapping.reference),
        ...headersOf(mapping.receivedAt),
        ...headersOf(mapping.originalTotal),
    ];
    const keyOf = (header: string): string => header.trim().toLowerCase();
    return read.filter((header, index) => read.findIndex((other) => keyOf(other) === keyOf(header)) === index);
};
